/**
 * useOnboardingThemeSettingsV2 Hook
 *
 * Lazy-loads V2 theme settings from the themeSettingsV2/ subcollection.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';

export interface UseOnboardingThemeSettingsV2Options {
  /** Project ID */
  projectId: string;
  /** Onboarding ID */
  onboardingId: string;
  /** Skip initial load (default: false) */
  skipInitialLoad?: boolean;
}

export interface UseOnboardingThemeSettingsV2Result {
  /** V2 Theme settings (null if not loaded or not set) */
  themeSettings: OnboardingThemeSettingsV2 | null;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Refresh theme settings from server; returns the freshly-loaded settings */
  refresh: () => Promise<OnboardingThemeSettingsV2 | null>;
  /** Update theme settings */
  update: (settings: OnboardingThemeSettingsV2) => Promise<void>;
  /**
   * Replace the theme settings with an imported theme (Import V2 Theme JSON). The onboarding then no
   * longer comes from its recorded source theme, so `sourceThemeId` / `sourceThemeKind` are cleared.
   */
  importSettings: (settings: OnboardingThemeSettingsV2) => Promise<void>;
}

/**
 * Hook for lazy-loading V2 theme settings from themeSettingsV2/ subcollection.
 */
export function useOnboardingThemeSettingsV2(
  options: UseOnboardingThemeSettingsV2Options
): UseOnboardingThemeSettingsV2Result {
  const { projectId, onboardingId, skipInitialLoad = false } = options;

  const [themeSettings, setThemeSettings] = useState<OnboardingThemeSettingsV2 | null>(null);
  const [loading, setLoading] = useState(!skipInitialLoad);
  const [error, setError] = useState<string | null>(null);

  const onboardingServiceRef = useRef(new OnboardingService());

  const load = useCallback(async (): Promise<OnboardingThemeSettingsV2 | null> => {
    if (!projectId || !onboardingId) {
      setThemeSettings(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      const settings = await onboardingService.getThemeSettingsV2(projectId, onboardingId);
      setThemeSettings(settings);
      return settings;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load V2 theme settings';
      setError(message);
      setThemeSettings(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [projectId, onboardingId]);

  const update = useCallback(async (settings: OnboardingThemeSettingsV2): Promise<void> => {
    if (!projectId || !onboardingId) {
      throw new Error('Missing projectId or onboardingId');
    }

    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      await onboardingService.saveThemeSettingsV2(projectId, onboardingId, settings);
      setThemeSettings(settings);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save V2 theme settings';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId, onboardingId]);

  const importSettings = useCallback(async (settings: OnboardingThemeSettingsV2): Promise<void> => {
    await update(settings);
    try {
      await onboardingServiceRef.current.clearThemeSource(projectId, onboardingId);
    } catch (err) {
      // Tracking metadata only: the imported theme is saved; the picker may still mark the old source Current.
      console.warn('[useOnboardingThemeSettingsV2] imported, but the recorded source theme was not cleared:', err);
    }
  }, [onboardingId, projectId, update]);

  const refresh = useCallback(async (): Promise<OnboardingThemeSettingsV2 | null> => {
    return load();
  }, [load]);

  useEffect(() => {
    if (!skipInitialLoad) {
      load();
    }
  }, [load, skipInitialLoad]);

  return {
    themeSettings,
    loading,
    error,
    refresh,
    update,
    importSettings,
  };
}
